// The composer's one ordered item list (mixed-posts spec): photos, videos and links in any order,
// for GC create / edit / import and the personal wall. Android's `ComposerItem` mirrors this model.
import type { LinkPreview, PostItemInput, WallItem, WallPost } from "../../api/content";
import { codePointCount, cutToCodePoints as cut } from "./codePoints";
import { IMAGE_ACCEPT, imageMimeOf, isOversizedSvg, isSupportedVideoFile, isVideoFile } from "../../api/upload";
import { isGalleryItem, isVideoItem, kindOf } from "../../player";

/** Where the editor posts: a GC wall, the caller's own wall, or a typed (v2) News / Event post. */
export type WallTarget = { type: "gc"; convId: number } | { type: "personal" } | { type: "news" } | { type: "event" };

/** What a post's Edit opens: a wall editor target, or the book / offer composer (their own modals, not PostEditorModal). */
export type EditTarget = WallTarget | { type: "book" } | { type: "apopilo" };

/** Typed targets: v2 posts (create via POST v2, edit via PUT v2), photos + links, no videos. */
export const isTypedTarget = (t: WallTarget): t is { type: "news" } | { type: "event" } => t.type === "news" || t.type === "event";

/** 3a R5: Edit is offered on own WALL, NEWS, EVENT, BOOK and APOPILO posts (a book / an offer opens its own composer, the callers check the author); BLOG has no editor yet. */
export const canEditPost = (p: { type?: string | null }): boolean => p.type == null || p.type === "WALL" || p.type === "NEWS" || p.type === "EVENT" || p.type === "BOOK" || p.type === "APOPILO";

/** The editor target for a typed post's edit (type never changes); null for WALL posts (the caller's wall decides). */
export function editTargetOf(p: { type?: string | null }): EditTarget | null {
  if (p.type === "NEWS") return { type: "news" };
  if (p.type === "EVENT") return { type: "event" };
  if (p.type === "BOOK") return { type: "book" };
  if (p.type === "APOPILO") return { type: "apopilo" };
  return null;
}

export const MAX_ITEMS = 100;
/** Max code points of a client-sent link item title (room-stc INVALID_FIELD above). */
export const MAX_ITEM_TITLE = 120;
/** The photo picker's accept list: every image type the web uploads (api/upload IMAGE_ACCEPT). */
export const PHOTO_TYPES = IMAGE_ACCEPT.split(",");
/** Video uploads (spec 2026-10-23): both walls take them; file-stc MaxVideo is 1 GB, the upload queue enforces it. */
export const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
/** What the editor's picker accepts: photos and videos, any mix. */
export const MEDIA_TYPES = [...PHOTO_TYPES, ...VIDEO_TYPES];
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

export interface ComposerRules {
  links: boolean; titleField: boolean; textOnly: boolean; captionMax: number;
  /** News: Post waits for a non-blank title (controller ruling: title required, items optional). */
  titleRequired?: boolean;
}

export function composerRules(target: WallTarget): ComposerRules {
  if (target.type === "gc") return { links: true, titleField: true, textOnly: false, captionMax: 2000 };
  // News (v2 post): a required title; photos + links optional (text-only is fine); no videos (they need the queued v1 wall path).
  // Event (3a R4): the same item rules as News.
  if (isTypedTarget(target)) return { links: true, titleField: true, titleRequired: true, textOnly: true, captionMax: 4096 };
  return { links: false, titleField: false, textOnly: true, captionMax: 4096 };
}

/** A new video's send, as the upload queue reports it (two phases: compress, then upload). */
export type VideoProgress = { phase: "compressing" | "uploading"; percent: number; sent: number; total: number };

export type FileItem = {
  key: number; kind: "file"; type: "PHOTO" | "VIDEO"; status: "uploading" | "done" | "error";
  fileId?: string; thumbnailFileId?: string; previewUrl?: string; name?: string; durationMs?: number | null; error?: string;
  /** A new video: its upload-queue item. Its status, progress and ids come from the queue (`withVideoSend`). */
  uploadId?: string;
  progress?: VideoProgress;
  /** A failed video that may still be retried (within 10 minutes of the failure). */
  retryable?: boolean;
};
/**
 * A link row. `title` is what is sent; `linkTitle` is the resolver's title (what "reset" restores);
 * `titleEdited` is set only by the title input (`editTitle`), never by a prefill or a resolve: an
 * import's source title may differ from the re-resolved one without the user having typed anything.
 */
export type LinkItem = {
  key: number; kind: "link"; url: string; status: "idle" | "checking" | "ok" | "error";
  preview?: LinkPreview; error?: string; title: string; linkTitle: string | null; titleEdited: boolean; durationMs?: number | null;
};
export type ComposerItem = FileItem | LinkItem;

let seq = 0;
export const nextKey = (): number => ++seq;

/**
 * The editor's items for an edit (links already resolved) or an import (links re-resolved by the editor).
 * Nothing is ever dropped (Android PostEditorVM.prefill): a stored item the editor cannot send again shows
 * as a failed row the user must remove, so a save can never delete it unnoticed.
 */
export function itemsFromPost(items: WallItem[], target: WallTarget, mode: "edit" | "import"): ComposerItem[] {
  const out: ComposerItem[] = [];
  for (const it of items) {
    if (isGalleryItem(it)) {
      const previewUrl = (isVideoItem(it) ? it.thumbnailUrl : it.photoUrl) ?? undefined;
      const base = { key: nextKey(), kind: "file" as const, type: it.type as "PHOTO" | "VIDEO", previewUrl, durationMs: it.durationMs };
      // No fileId: it cannot be re-submitted (Android: a failed tile, "upload failed").
      if (it.fileId == null) out.push({ ...base, status: "error", error: "wall_photo_upload_failed" });
      else out.push({ ...base, status: "done", fileId: it.fileId, thumbnailFileId: it.thumbnailFileId ?? undefined });
    } else {
      // Old YouTube items may carry only the video id (as the dock plays them).
      const url = kindOf(it).playUrl(it);
      const base = { key: nextKey(), kind: "link" as const, url: url ?? "", title: it.title ?? "", linkTitle: null, titleEdited: false, durationMs: it.durationMs };
      // A personal wall takes no links. Android refuses only on import (an edit keeps the row and the server
      // decides); the web personal composer has no link rows, so an edit shows it refused too, with the same
      // error, for the user to remove.
      if (target.type === "personal") {
        out.push({ ...base, status: "error", error: "ITEM_NOT_ALLOWED" });
        continue;
      }
      // No URL to send: a failed row to remove (never a blank row, which a post leaves out).
      if (url == null) {
        out.push({ ...base, status: "error", error: "LINK_UNREACHABLE" });
        continue;
      }
      out.push(
        mode === "edit"
          ? { ...base, status: "ok", preview: { type: it.type, provider: it.provider, codec: it.codec, live: it.live, url, externalId: it.externalId, title: it.title, thumbnailUrl: it.thumbnailUrl } }
          : { ...base, status: "checking" },
      );
    }
  }
  return out;
}

/** An import's post title and caption: GC keeps both (caption cut to 2000); personal merges the title into the caption. */
export function prefillText(source: WallPost, target: WallTarget): { title: string; caption: string } {
  const { captionMax } = composerRules(target);
  if (target.type === "gc") return { title: source.title ?? "", caption: cut(source.caption ?? "", captionMax) };
  const title = source.title?.trim() ?? "";
  const caption = source.caption ?? "";
  const merged = title ? (caption.trim() ? `${title}\n\n${caption}` : title) : caption;
  return { title: "", caption: cut(merged, captionMax) };
}

export function newLink(): LinkItem {
  return { key: nextKey(), kind: "link", url: "", status: "idle", title: "", linkTitle: null, titleEdited: false };
}

/** Tiles for picked photos and videos (at most `room`); invalid ones are error tiles, valid ones carry the file to upload. */
export function filesFromPicked(files: File[], room: number): (FileItem & { file?: File })[] {
  return files.slice(0, Math.max(0, room)).map((f) => {
    const key = nextKey();
    // A video goes to the app-wide upload queue, which checks its size (1 GB) and compresses it.
    // By its type, else (empty or application/octet-stream) its extension.
    if (isSupportedVideoFile(f)) return { key, kind: "file", type: "VIDEO", status: "uploading", name: f.name, file: f };
    // Any other video (MKV, AVI …) is refused here, before any upload.
    if (isVideoFile(f)) return { key, kind: "file", type: "VIDEO", status: "error", name: f.name, error: "video_type_not_supported" };
    if (!imageMimeOf(f)) return { key, kind: "file", type: "PHOTO", status: "error", name: f.name, error: "wall_photo_bad_type" };
    // file-stc caps an SVG at 1 MiB (413): refused here, with its own message.
    if (isOversizedSvg(f)) return { key, kind: "file", type: "PHOTO", status: "error", name: f.name, error: "image_svg_too_large" };
    if (f.size > MAX_PHOTO_BYTES) return { key, kind: "file", type: "PHOTO", status: "error", name: f.name, error: "wall_photo_too_large" };
    return { key, kind: "file", type: "PHOTO", status: "uploading", name: f.name, file: f };
  });
}

export function moveItem(items: ComposerItem[], from: number, to: number): ComposerItem[] {
  if (from === to || from < 0 || from >= items.length || to < 0 || to >= items.length) return items;
  const out = items.slice();
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved);
  return out;
}

/** Patch the item with `key` wherever it is now (late upload / resolve results land on their own item). */
export function patchItem(items: ComposerItem[], key: number, patch: Partial<FileItem> | Partial<LinkItem>): ComposerItem[] {
  return items.map((it) => (it.key === key ? ({ ...it, ...patch, key: it.key, kind: it.kind } as ComposerItem) : it));
}

/** A resolve result: never overwrites a non-empty (prefilled or typed) title. */
export function applyResolved(item: LinkItem, preview: LinkPreview): LinkItem {
  return {
    ...item, status: "ok", preview, error: undefined, linkTitle: preview.title,
    title: item.title === "" ? (preview.title ?? "") : item.title,
  };
}

export const titleOver = (item: LinkItem): boolean => codePointCount(item.title.trim()) > MAX_ITEM_TITLE;
/** The only blocking title case: the user typed a title over 120. */
export const titleTooLong = (item: LinkItem): boolean => item.titleEdited && titleOver(item);
export const editTitle = (item: LinkItem, title: string): LinkItem => ({ ...item, title, titleEdited: true });
export const resetTitle = (item: LinkItem): LinkItem => ({ ...item, title: item.linkTitle ?? "", titleEdited: false });

/** A link row with no URL yet: neither bad nor postable, it is left out of the post (Android PostEditorVM). */
const isBlankLink = (it: ComposerItem): boolean => it.kind === "link" && it.url.trim() === "" && !it.preview && it.status !== "error";

/** Mirrors room-stc DeriveKind over the items sent: no items or only photos/videos → PHOTO, else MEDIA. */
export function deriveKind(items: ComposerItem[]): "PHOTO" | "MEDIA" {
  return items.every((it) => it.kind === "file" || isBlankLink(it)) ? "PHOTO" : "MEDIA";
}

/**
 * The request items. A link always carries its trimmed title when ≤ 120 code points; an unedited
 * title over 120 is omitted so the server keeps the resolver's (an edited one blocks before this).
 */
export function toPostItems(items: ComposerItem[]): PostItemInput[] {
  const out: PostItemInput[] = [];
  for (const it of items) {
    if (it.kind === "file") {
      if (it.fileId == null) continue;
      out.push(it.thumbnailFileId != null ? { fileId: it.fileId, thumbnailFileId: it.thumbnailFileId } : { fileId: it.fileId });
    } else {
      if (isBlankLink(it)) continue;
      const url = it.preview?.url ?? it.url;
      out.push(titleOver(it) ? { url } : { url, title: it.title.trim() });
    }
  }
  return out;
}

export type Blocker =
  | { kind: "uploading"; done: number; total: number }
  /** A new video failed: Post reads "Fix N video(s) to post" until it is retried or removed. */
  | { kind: "failedVideos"; count: number }
  | { kind: "failedFiles" }
  | { kind: "links"; count: number }
  | { kind: "titles"; count: number }
  /** Links still resolving: Post waits, with its normal label (nothing to fix yet). */
  | { kind: "resolving" }
  | { kind: "empty" }
  /** News without a title: Post keeps its label, disabled. */
  | { kind: "noTitle" }
  | null;

/**
 * Why Post is disabled, in label precedence order; null when it can be sent. Mirrors Android
 * PostEditorVM: `badLinks` = non-blank links that failed to resolve or carry a typed title over 120
 * ("Fix N links" when any failed, else "Fix N titles"); a resolving link only waits; a blank row
 * is ignored (left out of the post).
 */
export function blockerOf(items: ComposerItem[], caption: string, rules: ComposerRules, title = ""): Blocker {
  const files = items.filter((it): it is FileItem => it.kind === "file");
  const links = items.filter((it): it is LinkItem => it.kind === "link" && !isBlankLink(it));
  if (files.some((f) => f.status === "uploading")) {
    const live = files.filter((f) => f.status !== "error");
    return { kind: "uploading", done: live.filter((f) => f.status === "done").length, total: live.length };
  }
  const failedVideos = files.filter((f) => f.status === "error" && f.uploadId != null).length;
  if (failedVideos > 0) return { kind: "failedVideos", count: failedVideos };
  if (files.some((f) => f.status === "error")) return { kind: "failedFiles" };
  const bad = links.filter((l) => l.status === "error" || titleTooLong(l)).length;
  if (links.some((l) => l.status === "error")) return { kind: "links", count: bad };
  if (bad > 0) return { kind: "titles", count: bad };
  if (links.some((l) => l.status !== "ok")) return { kind: "resolving" };
  if (rules.titleRequired) return title.trim() === "" ? { kind: "noTitle" } : null;
  if (files.length === 0 && links.length === 0 && !(rules.textOnly && caption.trim() !== "")) return { kind: "empty" };
  return null;
}

/** What `withVideoSend` reads from an upload-queue item (store/uploadQueue UploadItem). */
export interface VideoSendState {
  phase: "waiting" | "compressing" | "uploading" | "done" | "error" | "cancelled";
  progress: number;
  bytesSent: number;
  bytesTotal: number;
  posterUrl?: string;
  durationMs?: number;
  errorCode?: "TOO_LARGE" | "FAILED";
  errorDetail?: "BROWSER_LIMIT";
  retryable: boolean;
}

/**
 * A new video item as its send stands now: the queue's confirmed ids once READY (`done`), else its
 * phase and progress. Kept videos (no uploadId) and other items are returned as they are.
 */
export function withVideoSend(
  item: ComposerItem,
  send: VideoSendState | undefined,
  done: { fileId: string; thumbnailFileId: string; durationMs: number } | undefined,
): ComposerItem {
  if (item.kind !== "file" || item.uploadId == null) return item;
  const previewUrl = send?.posterUrl ?? item.previewUrl;
  if (done) {
    return {
      ...item, status: "done", fileId: done.fileId, thumbnailFileId: done.thumbnailFileId, previewUrl,
      durationMs: done.durationMs || item.durationMs, progress: undefined, error: undefined, retryable: false,
    };
  }
  if (!send || send.phase === "error" || send.phase === "cancelled") {
    const error =
      send?.errorDetail === "BROWSER_LIMIT" ? "video_too_large_browser"
        : send?.errorCode === "TOO_LARGE" ? "video_too_large"
          : send?.retryable ? "wall_editor_upload_failed_retry" : "wall_photo_upload_failed";
    return { ...item, status: "error", previewUrl, progress: undefined, error, retryable: !!send?.retryable };
  }
  const pct = Math.max(0, Math.min(100, Math.round(send.progress * 100)));
  const progress: VideoProgress =
    send.phase === "uploading"
      ? { phase: "uploading", percent: pct, sent: send.bytesSent, total: send.bytesTotal }
      : { phase: "compressing", percent: send.phase === "waiting" ? 0 : pct, sent: 0, total: send.bytesTotal };
  // "done" here is the moment between the queue's READY and its hand-off: still sending.
  return { ...item, status: "uploading", previewUrl, durationMs: send.durationMs ?? item.durationMs, progress, error: undefined, retryable: false };
}

/**
 * Whether closing the editor now queues the post (spec 2026-10-23 web): some new video is still
 * sending, no photo is, and the post would be ready to send once those videos are READY.
 */
export function canQueuePost(items: ComposerItem[], caption: string, rules: ComposerRules, title = ""): boolean {
  const files = items.filter((it): it is FileItem => it.kind === "file");
  if (!files.some((f) => f.status === "uploading" && f.uploadId != null)) return false;
  if (files.some((f) => f.status === "uploading" && f.uploadId == null)) return false;
  const asReady = items.map((it) => (it.kind === "file" && it.uploadId != null && it.status === "uploading" ? { ...it, status: "done" as const } : it));
  return blockerOf(asReady, caption, rules, title) === null;
}
